import {
    Box,
    Button,
    Card,
    CardActionArea,
    Link,
    PaletteColor,
    Stack,
    Typography,
} from '@mui/material';
import { Dns, Savings, Settings, Tag } from '@mui/icons-material';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { RestrictedComponent } from '../app/components/RestrictedComponent.tsx';
import { OperatorAppRoutes } from '../app/config/OperatorRoutingConfig.tsx';
import { SvgIcon } from 'dg-web-shared/ui/icons/SvgIcon.tsx';
import { SxProps } from '@mui/system';
import { Theme, useTheme } from '@mui/material/styles';
import { CountersOverviewTitle } from '../cloud-connector/CloudConnectorCounterOverview.tsx';
import { type JSX, useEffect, useState } from 'react';
import {
    RequestStatus,
    useServerFetch,
    useServerSuccessEffect,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { useUpdate } from 'dg-web-shared/lib/Flux.tsx';
import * as AuthenticationActions from '../app/actions/AuthenticationActions.ts';

interface PeripheryEventsCounter {
    alertsCount: number;
    warningsCount: number;
}

export function MobileHomePage() {
    const { operatorData } = useOperatorContext();
    if (operatorData.hasCloudConnector) {
        return <MobileHomePageContent />;
    }
    return <MobileNotAllowedModal />;
}

function MobileHomePageContent() {
    const { operatorData, currentLogin } = useOperatorContext();
    const navigate = useNavigate();
    const theme = useTheme();
    const iconStyle: SxProps<Theme> = {
        fontSize: '32px',
        color: theme => theme.palette.primary.main,
    };

    const [refetchCounter, setRefetchCounter] = useState(0);
    const [eventsCounters, setEventCounters] = useState<PeripheryEventsCounter>(
        { alertsCount: 0, warningsCount: 0 },
    );
    const [peripheryEventsCounter] = useServerFetch<
        PeripheryEventsCounter,
        { operatorId: number; refetchCounter: number }
    >(
        ({ operatorId }) => ({
            url: `/ui-api/operator-account/${operatorId}/periphery-events-counter`,
        }),
        { operatorId: currentLogin.mandantId, refetchCounter: refetchCounter },
    );

    useEffect(() => {
        if (
            peripheryEventsCounter.status === RequestStatus.SUCCESS ||
            peripheryEventsCounter.status === RequestStatus.ERROR
        ) {
            const timeout = setTimeout(() => {
                setRefetchCounter(prevCount => prevCount + 1);
            }, 5000);
            return () => clearTimeout(timeout);
        }
        return undefined;
    }, [peripheryEventsCounter.status, refetchCounter]);

    useServerSuccessEffect(peripheryEventsCounter, data => {
        setEventCounters(data);
    });

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflowY: 'auto',
            }}
        >
            <Header />
            <Box
                sx={{
                    padding: theme => theme.spacing(4),
                    backgroundColor: theme => theme.palette.blue.light,
                    flexGrow: 1,
                }}
            >
                <Box
                    sx={theme => ({
                        paddingTop: theme.spacing(1),
                        paddingBottom: theme.spacing(3),
                        textAlign: 'center',
                        marginBottom: theme.spacing(1),
                        color: theme => theme.palette.primary.main,
                    })}
                >
                    <Typography
                        variant="h2"
                        sx={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            fontSize: '34px',
                            fontWeight: 700,
                        }}
                    >
                        {operatorData.operatorName}
                    </Typography>
                    {operatorData.organizationLoginHasMultipleMobileLogins && (
                        <Box
                            onClick={() => navigate('/mobile/select-operator')}
                            sx={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                fontWeight: 600,
                            }}
                        >
                            <Localized
                                de="Konto wechseln"
                                fr="Changer de compte"
                                it="Cambia conto"
                                en="Switch account"
                            />
                        </Box>
                    )}
                </Box>
                <Stack spacing={2}>
                    <RestrictedComponent
                        route={
                            OperatorAppRoutes.MobileCloudConnectorPeripheries
                        }
                    >
                        <MenuItem
                            to="offstreet-devices"
                            label={
                                <Localized
                                    de="Geräte"
                                    fr="Appareils"
                                    it="Apparecchi"
                                    en="Devices"
                                />
                            }
                            customElementRight={
                                <Box sx={{ display: 'flex' }}>
                                    {eventsCounters.warningsCount > 0 && (
                                        <CounterDisplay
                                            counter={
                                                eventsCounters.warningsCount
                                            }
                                            color={theme.palette.warning}
                                        />
                                    )}
                                    {eventsCounters.alertsCount > 0 && (
                                        <CounterDisplay
                                            counter={eventsCounters.alertsCount}
                                            color={theme.palette.error}
                                        />
                                    )}
                                </Box>
                            }
                            icon={<Dns sx={iconStyle} />}
                        />
                    </RestrictedComponent>
                    <RestrictedComponent
                        route={OperatorAppRoutes.MobileCloudCounterOverview}
                    >
                        <MenuItem
                            to="counter"
                            label={<CountersOverviewTitle />}
                            icon={<Tag sx={iconStyle} />}
                        />
                    </RestrictedComponent>
                    <RestrictedComponent
                        route={OperatorAppRoutes.CloudConnectorRecode}
                    >
                        <MenuItem
                            to="recode"
                            label={
                                <Localized
                                    de="Rabatt erfassen"
                                    fr="Saisir rabais"
                                    it="Registra ribasso"
                                    en="Record discount"
                                />
                            }
                            icon={<Savings sx={iconStyle} />}
                        />
                    </RestrictedComponent>
                </Stack>
            </Box>
        </Box>
    );
}

function CounterDisplay({
    counter,
    color,
}: {
    counter: number;
    color: PaletteColor;
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                minWidth: 24,
                height: 24,
                alignContent: 'center',
                justifyContent: 'center',
                flexWrap: 'wrap',
                color: color.contrastText,
                borderRadius: '4px',
                marginRight: '5px',
                fontSize: '16px',
                fontWeight: 700,
                backgroundColor: color.main,
                '.menuItemActionArea:hover &': {
                    backgroundColor: color.contrastText,
                    color: color.main,
                },
            }}
        >
            {counter}
        </Box>
    );
}

function MenuItem({
    to,
    label,
    icon,
    customElementRight,
}: {
    to: string;
    label: JSX.Element;
    icon: JSX.Element;
    customElementRight?: JSX.Element;
}) {
    return (
        <Link component={RouterLink} to={to} underline="none">
            <Card elevation={0} sx={{ border: 0 }}>
                <CardActionArea
                    className={'menuItemActionArea'}
                    sx={theme => ({
                        display: 'flex',
                        justifyContent: 'space-between',
                        paddingX: theme.spacing(2),
                        paddingY: theme.spacing(3),
                        backgroundColor: theme =>
                            theme.palette.primary.contrastText,
                        ['&:hover']: {
                            backgroundColor: theme =>
                                theme.palette.primary.light,
                        },
                    })}
                >
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        {icon}
                        <Typography
                            sx={{
                                marginLeft: '24px',
                                fontSize: '20px',
                                fontWeight: 700,
                                color: theme => theme.palette.primary.main,
                            }}
                        >
                            {label}
                        </Typography>
                        <Box sx={{ marginLeft: 'auto' }}>
                            {customElementRight}
                        </Box>
                    </Box>
                </CardActionArea>
            </Card>
        </Link>
    );
}

function Header() {
    const navigate = useNavigate();
    const {
        operatorData: { operatorExternalId },
    } = useOperatorContext();
    return (
        <Box
            sx={theme => ({
                display: 'flex',
                width: '100%',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                padding: theme => theme.spacing(3),
            })}
        >
            <Box
                onClick={() => navigate(`/${operatorExternalId}/mobile`)}
                sx={{
                    width: 246,
                    height: 50,
                    paddingRight: theme => theme.spacing(2),
                }}
            >
                <ParkingportalLogo />
            </Box>
            <Link component={RouterLink} to={'settings'}>
                <Button
                    sx={{
                        width: 40,
                        height: 40,
                        minWidth: 40,
                        backgroundColor: theme =>
                            theme.palette.primary.contrastText,
                        ['&:hover']: {
                            backgroundColor: theme =>
                                theme.palette.primary.light,
                        },
                    }}
                >
                    <Settings />
                </Button>
            </Link>
        </Box>
    );
}

export function ParkingportalLogo() {
    return <SvgIcon icon={'parkingportal'} width={507} height={103} />;
}

function MobileNotAllowedModal() {
    const update = useUpdate();
    return (
        <ConfirmationDialog
            open
            title={
                <Localized
                    de="Nicht erlaubt"
                    fr="Pas autorisé"
                    it="Non contentito"
                    en="Not allowed"
                />
            }
            confirmLabel={
                <Localized
                    de="Abmelden"
                    fr="Déconnecter"
                    it="Esci"
                    en="Logout"
                />
            }
            onConfirm={() => update(AuthenticationActions.logout)}
            dialogVersion={ConfirmationDialogVersions.ERROR}
        >
            <>
                <p>
                    <Localized
                        de="Mit diesen Anmeldedaten ist die Nutzung der mobilen App nicht erlaubt."
                        fr="Avec ces identifiants, il n'est pas possible d'utiliser l'app mobile."
                        it="Con queste credenziali non è consentito utilizzate l'app mobile."
                        en="With these credentials, you are not allowed to use the mobile app."
                    />
                </p>
                <p>
                    <Localized
                        de="Bitte melden Sie sich über den Desktop-Browser an."
                        fr="Veuillez vous connecter via le browser desktop."
                        it="Voglia accedere tramite il browser desktop."
                        en="Please access via your desktop browser."
                    />
                </p>
            </>
        </ConfirmationDialog>
    );
}
